import { Button, FormGroup, FormInput, Heading, Input, Modal } from '@snsw/react-component-library';
import { PlantDesignDrawingAndTechDetailsPage } from 'plantDesign/constants/plantDesignDrawingAndTechDetailsPage';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import styles from '../DrawingsAndTechDetails.module.scss';

export const TechStandardDetails = React.forwardRef((props, ref) => {
  const {
    index = 0,
    details = {},
    onChange = () => {},
    onErrorChange = () => {},
    showRemoveButton = false,
    onRemoveClick = () => {},
    triggerValidate = false,
  } = props;
  const { number: savedNumber = '', yearOfPublication: savedYearOfPublication = '' } = details;

  const [number, setNumber] = useState(savedNumber);
  const [yearOfPublication, setYearOfPublication] = useState(savedYearOfPublication);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const PAGE_CONSTANTS = PlantDesignDrawingAndTechDetailsPage.addTechStandard;

  const validators = [
    {
      fieldName: `${index}_number`,
      fieldValue: number,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Enter a technical standard number',
    },
    {
      fieldName: `${index}_yearOfPublication`,
      fieldValue: yearOfPublication,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Enter a year of publication',
    },
  ];

  const validate = () => {
    const isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

    setShowErrors(!isCurrentComponentValid);

    return isCurrentComponentValid;
  };

  const removeDetails = () => {
    setShowModal(true);
  };

  const cancelRemove = () => {
    setShowModal(false);
  };

  const confirmRemove = () => {
    setShowModal(false);
    onRemoveClick(index);
  };

  useImperativeHandle(ref, () => ({
    validate: () => validate(),
  }));

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  useEffect(() => {
    if (showErrors) validate();

    onChange({ number, yearOfPublication });
  }, [number, yearOfPublication]);

  useEffect(() => {
    if (triggerValidate) {
      validate();
    }
  }, [triggerValidate]);

  return (
    <>
      <Heading level={3} className={styles.marginTopTwoHalfRem}>
        {(index + 1).toString().padStart(2, '0')} Technical standard
      </Heading>
      <FormGroup
        id={index + '_number'}
        label={PAGE_CONSTANTS.numberLabel}
        helpMessage={PAGE_CONSTANTS.numberHelpMessage}
        isRequired
        errorMessage={errors[`${index}_number`]?.errorMessage}
        hasError={showErrors && errors[`${index}_number`]?.invalid}
      >
        <Input
          onChange={(e) => {
            setNumber(e.target.value);
          }}
          value={number}
          inputWidth="xl"
        />
      </FormGroup>
      <FormInput
        label={PAGE_CONSTANTS.yearOfPublicationLabel}
        id={index + '_yearOfPublication'}
        errorMessage={errors[`${index}_yearOfPublication`]?.errorMessage}
        hasError={showErrors && errors[`${index}_yearOfPublication`]?.invalid}
        value={yearOfPublication}
        onChange={(e, { value }) => setYearOfPublication(value)}
        inputWidth="xs"
        placeholder="YYYY"
        mask={[/\d/, /\d/, /\d/, /\d/]}
        margin={{ top: 'md' }}
      />

      {showRemoveButton && (
        <Button className={styles.marginTopOne} variant="link" onClick={removeDetails}>
          Remove
        </Button>
      )}
      {showModal && (
        <Modal
          title="Are you sure you want to remove this technical standard?"
          description="This will delete all details for the technical standard."
          buttons={[
            { text: 'Remove', onClick: confirmRemove },
            { text: 'Cancel', onClick: cancelRemove },
          ]}
        />
      )}
    </>
  );
});

TechStandardDetails.displayName = 'TechStandardDetails';
